import type { ReactElement } from "react";
import type {
  DeliveryPreference,
  InvitationLookupReady,
  MemberCompletionDetails,
  PublicationPreferences,
  ReferenceOption,
  RegistrationCompletionRequest,
  RegistrationVariant,
  VariantOptionCatalogs,
} from "./registration-invitation-client";

/**
 * Compiled variant form definitions (CLMS-113 T068, US3). One catalog of
 * approved, renderable field definitions per registration variant — the
 * client-side allowlist half of the editableFields intersection. Fields
 * render and submit ONLY when they appear in BOTH the compiled variant
 * definition below and the server `editableFields` dot-path list. No
 * identity, role, status, ID, or payment/provider field is ever defined
 * here, so none can ever render or submit regardless of what a server
 * response claims is editable.
 *
 * Variant field sets are legacy-parity, contract-corrected (validation
 * record us3-contract-corrections-2026-08-08), narrowed to the two supported
 * variants by 011-20: Direct = common + birth + publication + read-only
 * pre-approved staff counsel (NO expertise); Member = common + practice
 * areas + arbitrator listing + company size + publication preferences +
 * provider-neutral program selection.
 *
 * Reference-backed values are CLOSED vocabularies (validation record
 * us3-reference-catalog-and-success-contract-2026-08-09): every such field
 * renders only the server's variant-scoped `optionCatalogs` options and
 * submits only option references. There is no free-entry fallback; a
 * missing/empty required catalog is a contract error and the page fails
 * closed into the generic unavailable state (no partial form).
 */

export type VariantFormValues = Record<string, string | boolean | string[]>;

export type VariantFormSection =
  | "profile"
  | "business-address"
  | "mailing-address"
  | "details"
  | "publications";

export interface TextControl {
  kind: "text";
  type: "text" | "email" | "tel";
  autoComplete: string;
  /** Contract maxLength — enforced client-side so an over-length value
   * never reaches the network (the server maps it to a generic 400 that
   * would destroy the form for a fixable typo). */
  maxLength: number;
  pattern?: RegExp;
  patternMessage?: string;
  placeholder?: string;
}

export interface DeliverySelectControl {
  kind: "delivery-select";
}

export interface CheckboxControl {
  kind: "checkbox";
}

/** Keys into the ready lookup's variant-scoped `optionCatalogs` object. */
export type OptionCatalogKey =
  | "birthYearRangeReferences"
  | "companySizeReferences"
  | "countryReferences"
  | "practiceAreaReferences"
  | "memberProgramSelections"
  | "stateReferences";

/** Closed single-select over a server option catalog. The user picks a
 * label; the submitted value is the option `reference`. No free entry. */
export interface ReferenceSelectControl {
  kind: "reference-select";
  catalogKey: OptionCatalogKey;
  placeholder: string;
}

/** Closed multi-select checkbox group over a server option catalog. The
 * submitted value is the selected option `reference`s in catalog order. */
export interface ReferenceMultiControl {
  kind: "reference-multi";
  catalogKey: OptionCatalogKey;
  maxItems: number;
}

export type VariantFieldControl =
  | TextControl
  | DeliverySelectControl
  | CheckboxControl
  | ReferenceSelectControl
  | ReferenceMultiControl;

export interface VariantFieldDefinition {
  /** Canonical editableFields dot path (e.g. `fellowDetails.birthMonthDay`). */
  path: string;
  label: string;
  required: boolean;
  section: VariantFormSection;
  control: VariantFieldControl;
  /**
   * FR-028: the server declares this field's requiredness per lookup through
   * `requiredFields` (company-coverage evaluation). Rendering is still
   * governed by `editableFields` alone: omitted → nothing renders and the
   * path is never posted. When rendered, the field is required only if
   * `requiredFields` lists its path; otherwise (including a server that omits
   * `requiredFields` entirely) it keeps the legacy optional rendering.
   */
  coverageGated?: true;
}

export const DELIVERY_PREFERENCE_OPTIONS: ReadonlyArray<{
  value: DeliveryPreference;
  label: string;
}> = [
  { value: "none", label: "None" },
  { value: "digital", label: "Digital" },
  { value: "print", label: "Print" },
  { value: "both", label: "Print and digital" },
];

const BIRTH_MONTH_DAY_PATTERN = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

function text(
  path: string,
  label: string,
  section: VariantFormSection,
  options: Partial<Omit<TextControl, "kind">> & { required?: boolean } = {},
): VariantFieldDefinition {
  const { required = false, ...control } = options;
  return {
    path,
    label,
    required,
    section,
    control: {
      kind: "text",
      type: control.type ?? "text",
      autoComplete: control.autoComplete ?? "off",
      maxLength: control.maxLength ?? 100,
      pattern: control.pattern,
      patternMessage: control.patternMessage,
      placeholder: control.placeholder,
    },
  };
}

function locationFields(
  wrapper: "businessLocation" | "mailingLocation",
  section: VariantFormSection,
): VariantFieldDefinition[] {
  const scope = wrapper === "businessLocation" ? "work" : "home";
  return [
    text(`${wrapper}.addressLine1`, "Address line 1", section, {
      maxLength: 200,
      autoComplete: `section-${scope} address-line1`,
    }),
    text(`${wrapper}.addressLine2`, "Address line 2", section, {
      maxLength: 200,
      autoComplete: `section-${scope} address-line2`,
    }),
    text(`${wrapper}.city`, "City", section, {
      maxLength: 100,
      autoComplete: `section-${scope} address-level2`,
    }),
    {
      path: `${wrapper}.stateReference`,
      label: "State or province",
      required: false,
      section,
      control: {
        kind: "reference-select",
        catalogKey: "stateReferences",
        placeholder: "Select a state or province",
      },
    },
    text(`${wrapper}.postalCode`, "Postal code", section, {
      maxLength: 20,
      autoComplete: `section-${scope} postal-code`,
    }),
    {
      path: `${wrapper}.countryReference`,
      label: "Country",
      required: false,
      section,
      control: {
        kind: "reference-select",
        catalogKey: "countryReferences",
        placeholder: "Select a country",
      },
    },
  ];
}

const COMMON_PROFILE_FIELDS: readonly VariantFieldDefinition[] = [
  text("salutation", "Salutation", "profile", { maxLength: 20 }),
  text("firstName", "First name", "profile", {
    required: true,
    autoComplete: "given-name",
    maxLength: 100,
  }),
  text("lastName", "Last name", "profile", {
    required: true,
    autoComplete: "family-name",
    maxLength: 100,
  }),
  text("suffix", "Suffix", "profile", { maxLength: 30 }),
  text("email", "Email", "profile", {
    required: true,
    type: "email",
    autoComplete: "email",
    maxLength: 320,
  }),
  text("phone", "Work phone", "profile", { type: "tel", autoComplete: "tel", maxLength: 30 }),
  text("position", "Position / job title", "profile", {
    autoComplete: "organization-title",
    maxLength: 150,
  }),
  text("companyName", "Company", "profile", { autoComplete: "organization", maxLength: 200 }),
  ...locationFields("businessLocation", "business-address"),
  ...locationFields("mailingLocation", "mailing-address"),
];

function publicationPreferenceFields(
  wrapper: "fellowDetails" | "memberDetails",
): VariantFieldDefinition[] {
  const base = `${wrapper}.publicationPreferences`;
  return [
    { path: `${base}.clmMagazine`, label: "CLM Magazine" },
    { path: `${base}.constructionClaims`, label: "Construction Claims" },
    { path: `${base}.professionalTimes`, label: "Professional Times" },
  ].map(({ path, label }) => ({
    path,
    label,
    required: false,
    section: "publications" as const,
    control: { kind: "delivery-select" as const },
  }));
}

function birthFields(): VariantFieldDefinition[] {
  return [
    text("fellowDetails.birthMonthDay", "Birth month and day", "details", {
      maxLength: 5,
      placeholder: "MM-DD",
      pattern: BIRTH_MONTH_DAY_PATTERN,
      patternMessage: "Enter the month and day as MM-DD.",
    }),
    {
      path: "fellowDetails.birthYearRangeReference",
      label: "Birth year range",
      required: false,
      section: "details",
      control: {
        kind: "reference-select",
        catalogKey: "birthYearRangeReferences",
        placeholder: "Select a birth year range",
      },
    },
  ];
}

/** Compiled per-variant client allowlist: the only fields that can ever
 * render or submit for each resolved registration variant. */
export const VARIANT_FIELD_DEFINITIONS: Record<
  RegistrationVariant,
  readonly VariantFieldDefinition[]
> = {
  direct_fellow: [
    ...COMMON_PROFILE_FIELDS,
    ...birthFields(),
    ...publicationPreferenceFields("fellowDetails"),
  ],
  member: [
    ...COMMON_PROFILE_FIELDS,
    {
      path: "memberDetails.companySizeReference",
      label: "Company size",
      required: false,
      section: "details",
      control: {
        kind: "reference-select",
        catalogKey: "companySizeReferences",
        placeholder: "Select a company size",
      },
    },
    {
      path: "memberDetails.practiceAreaReferences",
      label: "Practice areas",
      required: false,
      section: "details",
      control: {
        kind: "reference-multi",
        catalogKey: "practiceAreaReferences",
        maxItems: 100,
      },
    },
    {
      path: "memberDetails.arbitratorListing",
      label: "Include me in the arbitrator listing",
      required: false,
      section: "details",
      control: { kind: "checkbox" },
    },
    ...publicationPreferenceFields("memberDetails"),
    {
      path: "memberDetails.memberProgramSelection",
      label: "Member program selection",
      required: false,
      section: "details",
      control: {
        kind: "reference-select",
        catalogKey: "memberProgramSelections",
        placeholder: "Select a member program",
      },
      coverageGated: true,
    },
  ],
};

/**
 * Runtime allowlist guard (011-20): the generated types only admit the two
 * supported variants, but a lookup payload is still runtime input — a
 * retired or otherwise unknown variant value must fail closed before any
 * render plan is compiled. Own-property check only: prototype-chain names
 * (`constructor`, `toString`, `__proto__`, …) must NOT classify as supported.
 */
export function isSupportedRegistrationVariant(variant: string): variant is RegistrationVariant {
  return Object.hasOwn(VARIANT_FIELD_DEFINITIONS, variant);
}

export interface VariantRenderPlan {
  variant: RegistrationVariant;
  /** Compiled ∩ server intersection, in compiled (render) order. */
  fields: readonly VariantFieldDefinition[];
  /** Server editableFields entries outside the contract catalog — ignored,
   * reported through safe telemetry by the form (field NAME only). Company/
   * location identity paths and staff-counsel office paths are never
   * editable per contract, so a server listing one lands here too. */
  unknownServerFields: readonly string[];
  /** Resolved closed options per rendered reference-backed path, from the
   * ready lookup's variant-scoped optionCatalogs. */
  options: ReadonlyMap<string, readonly ReferenceOption[]>;
  /** Rendered reference-backed paths whose required catalog is absent or
   * empty — a contract error. Any entry here makes the page fail closed
   * into the generic unavailable state; the form never renders. */
  missingCatalogPaths: ReadonlySet<string>;
}

function resolveCatalog(
  catalogs: VariantOptionCatalogs | undefined,
  key: OptionCatalogKey,
): readonly ReferenceOption[] | undefined {
  const options = (catalogs as Partial<Record<OptionCatalogKey, ReferenceOption[]>> | undefined)?.[
    key
  ];
  return Array.isArray(options) ? options : undefined;
}

/** Sentinel for the optional single-select clear affordance (legacy
 * "--Please Select--" parity). Radix SelectItem values must be non-empty
 * strings, so "no selection" round-trips through this sentinel and maps
 * back to the empty form value — which the catalog guard in the request
 * builder then omits from the completion request. A served catalog
 * reference equal to it would be indistinguishable from the clear
 * affordance, so such a catalog is rejected as malformed below. */
export const NO_SELECTION_VALUE = "__clm-no-selection__";

/**
 * 011-15 F5 (DECIDED 2026-09-04, amended): the Member program field renders
 * the server-provided catalog label VERBATIM — the SPA never humanizes or
 * substitutes display text. A catalog entry with a missing/blank label, a
 * label that merely echoes its reference (raw-code leak, e.g. "standard";
 * compared trimmed, since surrounding whitespace renders identically to the
 * raw code), or a reference colliding with the reserved no-selection
 * sentinel is a serving-contract error and FAILS CLOSED into the same
 * generic unavailable state as a missing catalog (the serving service owns
 * display labels).
 */
function isMalformedMemberProgramOption(option: ReferenceOption): boolean {
  return (
    option.label.trim() === "" ||
    option.label.trim() === option.reference.trim() ||
    option.reference === NO_SELECTION_VALUE
  );
}

export function compileVariantRenderPlan(
  variant: RegistrationVariant,
  editableFields: readonly string[],
  optionCatalogs: VariantOptionCatalogs | undefined,
  requiredFields?: readonly string[],
): VariantRenderPlan {
  const definitions = VARIANT_FIELD_DEFINITIONS[variant];
  const serverSet = new Set(editableFields);
  // Absent `requiredFields` (a server predating FR-028) means "no
  // server-declared required paths" — never a reason to hide anything.
  const requiredSet = new Set(requiredFields ?? []);
  const compiledPaths = new Set(definitions.map((definition) => definition.path));

  // Rendering is governed by `editableFields` alone: a path the server omits
  // renders nothing — never a disabled, hidden, or pre-populated control —
  // and is never posted. FR-028 then layers requiredness onto a rendered
  // coverage-gated definition: listed in `requiredFields` → required (label
  // marker, aria-required, no clear affordance); otherwise it keeps its
  // legacy optional rendering (clearable select). `requiredFields` can only
  // promote definitions that opt in via `coverageGated`; the schema
  // `required` arrays already cover every other path.
  const fields = definitions
    .filter((definition) => serverSet.has(definition.path))
    .map((definition) =>
      definition.coverageGated && !definition.required && requiredSet.has(definition.path)
        ? { ...definition, required: true }
        : definition,
    );
  const options = new Map<string, readonly ReferenceOption[]>();
  const missingCatalogPaths = new Set<string>();
  for (const definition of fields) {
    const control = definition.control;
    if (control.kind !== "reference-select" && control.kind !== "reference-multi") {
      continue;
    }
    const catalog = resolveCatalog(optionCatalogs, control.catalogKey);
    // EVERY clearable (optional) single-select prepends the reserved
    // no-selection sentinel item, so a served reference equal to it would
    // be indistinguishable from the clear affordance and a duplicate
    // SelectItem value — a serving-contract error for ANY such catalog,
    // not just the member program one.
    const sentinelCollision =
      control.kind === "reference-select" &&
      !definition.required &&
      catalog !== undefined &&
      catalog.some((option) => option.reference === NO_SELECTION_VALUE);
    if (
      catalog &&
      catalog.length > 0 &&
      !sentinelCollision &&
      !(
        control.catalogKey === "memberProgramSelections" &&
        catalog.some(isMalformedMemberProgramOption)
      )
    ) {
      options.set(definition.path, catalog);
    } else {
      // Absent, empty, or malformed (sentinel collision anywhere; label
      // quality for the member program) catalog: the page fails closed
      // into the generic unavailable state — never a partial form and
      // never a client-invented label.
      missingCatalogPaths.add(definition.path);
    }
  }

  return {
    variant,
    fields,
    unknownServerFields: editableFields.filter((field) => !compiledPaths.has(field)),
    options,
    missingCatalogPaths,
  };
}

/** The dot paths the current plan renders — used to split recognized 422
 * field errors (inline) from unrecognized ones (summary only). */
export function renderedFieldPaths(plan: VariantRenderPlan): ReadonlySet<string> {
  return new Set(plan.fields.map((definition) => definition.path));
}

type LookupPrefill = InvitationLookupReady["prefill"];

function prefillTextValue(path: string, prefill: LookupPrefill): string {
  switch (path) {
    case "salutation":
      return prefill.salutation ?? "";
    case "firstName":
      return prefill.firstName ?? "";
    case "lastName":
      return prefill.lastName ?? "";
    case "suffix":
      return prefill.suffix ?? "";
    case "email":
      return prefill.email ?? "";
    case "phone":
      return prefill.phone ?? "";
    case "position":
      return prefill.position ?? "";
    case "companyName":
      return prefill.company?.name ?? "";
    default:
      // Locations, birth, and reference fields have no lookup prefill in the
      // contract prefill shapes.
      return "";
  }
}

function prefillPublicationPreference(path: string, prefill: LookupPrefill): DeliveryPreference {
  const preferences: PublicationPreferences | undefined =
    "publicationPreferences" in prefill ? prefill.publicationPreferences : undefined;
  const key = path.slice(path.lastIndexOf(".") + 1) as keyof PublicationPreferences;
  return preferences?.[key] ?? "none";
}

function prefillReferenceSelections(path: string, prefill: LookupPrefill): string[] {
  if (path === "memberDetails.practiceAreaReferences" && "practiceAreaReferences" in prefill) {
    return prefill.practiceAreaReferences ?? [];
  }
  return [];
}

/** Single-select prefill — only a reference present in the served catalog is
 * ever selected, so a stale prefill can never submit an out-of-catalog value. */
function prefillReferenceSelect(
  path: string,
  prefill: LookupPrefill,
  options: readonly ReferenceOption[] | undefined,
): string {
  if (path !== "memberDetails.companySizeReference") {
    return "";
  }
  const sizeReference = "company" in prefill ? (prefill.company?.sizeReference ?? "") : "";
  return sizeReference && options?.some((option) => option.reference === sizeReference)
    ? sizeReference
    : "";
}

export function createVariantInitialValues(
  plan: VariantRenderPlan,
  prefill: LookupPrefill,
): VariantFormValues {
  const values: VariantFormValues = {};
  for (const definition of plan.fields) {
    switch (definition.control.kind) {
      case "text":
        values[definition.path] = prefillTextValue(definition.path, prefill);
        break;
      case "delivery-select":
        values[definition.path] = prefillPublicationPreference(definition.path, prefill);
        break;
      case "checkbox":
        values[definition.path] =
          "arbitratorListing" in prefill ? (prefill.arbitratorListing ?? false) : false;
        break;
      case "reference-select":
        values[definition.path] = prefillReferenceSelect(
          definition.path,
          prefill,
          plan.options.get(definition.path),
        );
        break;
      case "reference-multi": {
        const catalog = plan.options.get(definition.path) ?? [];
        const preferred = new Set(prefillReferenceSelections(definition.path, prefill));
        values[definition.path] = catalog
          .filter((option) => preferred.has(option.reference))
          .map((option) => option.reference);
        break;
      }
    }
  }
  return values;
}

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export type VariantFieldErrors = Partial<Record<string, string>>;

export function validateVariantValues(
  plan: VariantRenderPlan,
  values: VariantFormValues,
): VariantFieldErrors {
  const errors: VariantFieldErrors = {};

  for (const definition of plan.fields) {
    const control = definition.control;
    if (control.kind === "text") {
      const value = String(values[definition.path] ?? "").trim();
      if (definition.required && !value) {
        errors[definition.path] = `${definition.label} is required.`;
      } else if (value.length > control.maxLength) {
        errors[definition.path] =
          `${definition.label} must be ${control.maxLength} characters or fewer.`;
      } else if (value && control.pattern && !control.pattern.test(value)) {
        errors[definition.path] =
          control.patternMessage ?? `${definition.label} has an invalid format.`;
      } else if (value && control.type === "email" && !EMAIL_PATTERN.test(value)) {
        errors[definition.path] = "Enter a valid email address.";
      }
    } else if (control.kind === "reference-select") {
      // FR-028: a server-required single-select (e.g. the member program for
      // an uncovered company) blocks completion until a catalog value is
      // chosen; the message names the field only.
      if (definition.required && !selectedReference(plan, values, definition.path)) {
        errors[definition.path] = `${definition.label} is required.`;
      }
    } else if (control.kind === "reference-multi") {
      const selected = selectedReferences(plan, values, definition.path);
      if (selected.length > control.maxItems) {
        errors[definition.path] =
          `${definition.label} accepts at most ${control.maxItems} selections.`;
      }
    }
  }

  return errors;
}

type EditableProfilePayload = Extract<
  RegistrationCompletionRequest,
  { profile: unknown }
>["profile"];
type EditableLocationPayload = NonNullable<EditableProfilePayload["businessLocation"]>;

function textValue(values: VariantFormValues, path: string): string {
  return String(values[path] ?? "").trim();
}

/** Selected catalog references for a closed multi-select, canonicalized to
 * catalog order so identical selections always submit identically. A path
 * whose catalog is missing (contract error state) never submits values. */
export function selectedReferences(
  plan: VariantRenderPlan,
  values: VariantFormValues,
  path: string,
): string[] {
  const catalog = plan.options.get(path);
  if (!catalog) {
    return [];
  }
  const raw = values[path];
  const selected = new Set(Array.isArray(raw) ? raw : []);
  return catalog.filter((option) => selected.has(option.reference)).map((o) => o.reference);
}

/** Selected catalog reference for a closed single-select — only a value that
 * exists in the served catalog is ever submitted. */
function selectedReference(
  plan: VariantRenderPlan,
  values: VariantFormValues,
  path: string,
): string {
  const catalog = plan.options.get(path);
  if (!catalog) {
    return "";
  }
  const value = textValue(values, path);
  return catalog.some((option) => option.reference === value) ? value : "";
}

function buildLocation(
  wrapper: "businessLocation" | "mailingLocation",
  plan: VariantRenderPlan,
  rendered: ReadonlySet<string>,
  values: VariantFormValues,
  cleared: ReadonlySet<string>,
): EditableLocationPayload | undefined {
  const location: EditableLocationPayload = {};
  let hasEntry = false;
  for (const key of ["addressLine1", "addressLine2", "city", "postalCode"] as const) {
    const path = `${wrapper}.${key}`;
    if (rendered.has(path)) {
      const value = textValue(values, path);
      if (value) {
        location[key] = value;
        hasEntry = true;
      } else if (cleared.has(path)) {
        // Tri-state contract: explicit null clears the stored value;
        // omission is no-change (never-populated fields stay omitted).
        location[key] = null;
        hasEntry = true;
      }
    }
  }
  // State/country are closed geography selects: only a reference present in
  // the served catalog is ever submitted. Location identity (`.reference`)
  // is server-derived and never built here.
  for (const key of ["stateReference", "countryReference"] as const) {
    const path = `${wrapper}.${key}`;
    if (rendered.has(path)) {
      const value = selectedReference(plan, values, path);
      if (value) {
        location[key] = value;
        hasEntry = true;
      } else if (cleared.has(path)) {
        location[key] = null;
        hasEntry = true;
      }
    }
  }
  return hasEntry ? location : undefined;
}

function buildProfile(
  plan: VariantRenderPlan,
  rendered: ReadonlySet<string>,
  values: VariantFormValues,
  cleared: ReadonlySet<string>,
): EditableProfilePayload {
  const profile: Partial<EditableProfilePayload> = {};

  // Every profile key — including the schema-required name/email trio —
  // submits ONLY when the server listed it as editable; the contract treats
  // absent fields as no-change and rejects paths outside its returned set,
  // so an empty-string placeholder is never posted for an unlisted field.
  for (const key of ["firstName", "lastName", "email"] as const) {
    if (rendered.has(key)) {
      profile[key] = textValue(values, key);
    }
  }
  // Nullable optional keys are tri-state: a non-empty value updates, an
  // explicit user clear of a previously populated value submits null, and
  // an untouched/never-populated field is omitted (no-change).
  for (const key of ["salutation", "suffix", "phone", "position", "companyName"] as const) {
    if (rendered.has(key)) {
      const value = textValue(values, key);
      if (value) {
        profile[key] = value;
      } else if (cleared.has(key)) {
        profile[key] = null;
      }
    }
  }

  const businessLocation = buildLocation("businessLocation", plan, rendered, values, cleared);
  if (businessLocation) {
    profile.businessLocation = businessLocation;
  }
  const mailingLocation = buildLocation("mailingLocation", plan, rendered, values, cleared);
  if (mailingLocation) {
    profile.mailingLocation = mailingLocation;
  }

  return profile as EditableProfilePayload;
}

function buildPublicationPreferences(
  wrapper: "fellowDetails" | "memberDetails",
  rendered: ReadonlySet<string>,
  values: VariantFormValues,
): PublicationPreferences | undefined {
  const preferences: PublicationPreferences = {};
  let hasValue = false;
  for (const key of ["clmMagazine", "constructionClaims", "professionalTimes"] as const) {
    const path = `${wrapper}.publicationPreferences.${key}`;
    if (rendered.has(path)) {
      preferences[key] = (values[path] ?? "none") as DeliveryPreference;
      hasValue = true;
    }
  }
  return hasValue ? preferences : undefined;
}

/**
 * Builds the exact contract completion request for the resolved variant:
 * only paths in the compiled ∩ server intersection are submitted (the server
 * rejects any posted path outside its returned set). Reference-backed values
 * submit only option references from the served closed catalogs, with
 * multi-selects canonicalized to catalog order. Nullable paths follow the
 * server's tri-state semantics: absent = no-change, null = explicit clear,
 * value = update. `clearedPaths` carries the rendered paths the user
 * explicitly cleared after they held a value (prefilled or user-entered);
 * those submit null, while never-populated fields stay omitted.
 * Company/location identity and the Direct Fellow staff-counsel office
 * scope are server authority and are NEVER echoed in any completion
 * request (the stored approved_office_scope stays display-only).
 */
export function buildVariantCompletionRequest(
  lookup: InvitationLookupReady,
  proof: string,
  plan: VariantRenderPlan,
  values: VariantFormValues,
  clearedPaths: ReadonlySet<string> = new Set(),
  /** FR-050: the member's new password; only the `initial` submission carries it. */
  password = "",
): RegistrationCompletionRequest {
  const rendered = renderedFieldPaths(plan);
  // Only rendered paths can ever clear: a path outside the compiled ∩
  // server intersection is never submitted in any state.
  const cleared = new Set([...clearedPaths].filter((path) => rendered.has(path)));
  const profile = buildProfile(plan, rendered, values, cleared);

  switch (lookup.registrationVariant) {
    case "direct_fellow": {
      // No staff-counsel echo: lookup and completion both authorize from the
      // invitation's immutable approved_office_scope; the office renders
      // display-only and never appears in the request.
      return {
        proof,
        registrationVariant: "direct_fellow",
        profile,
        fellowDetails: {
          ...buildFellowBirth(plan, rendered, values, cleared),
          ...wrapPublicationPreferences("fellowDetails", rendered, values),
        },
      };
    }
    case "member": {
      const memberProgramSelection = selectedReference(
        plan,
        values,
        "memberDetails.memberProgramSelection",
      );
      const companySizeReference = selectedReference(
        plan,
        values,
        "memberDetails.companySizeReference",
      );
      // Contract-required keys are always present; nullable optional keys
      // are tri-state — value = update, explicit clear = null, otherwise
      // omitted (no-change).
      const memberDetails: MemberCompletionDetails = {
        practiceAreaReferences: rendered.has("memberDetails.practiceAreaReferences")
          ? selectedReferences(plan, values, "memberDetails.practiceAreaReferences")
          : [],
        arbitratorListing: rendered.has("memberDetails.arbitratorListing")
          ? values["memberDetails.arbitratorListing"] === true
          : false,
        publicationPreferences:
          buildPublicationPreferences("memberDetails", rendered, values) ?? {},
      };
      if (companySizeReference) {
        memberDetails.companySizeReference = companySizeReference;
      } else if (cleared.has("memberDetails.companySizeReference")) {
        memberDetails.companySizeReference = null;
      }
      if (memberProgramSelection) {
        memberDetails.memberProgramSelection = memberProgramSelection;
      } else if (cleared.has("memberDetails.memberProgramSelection")) {
        memberDetails.memberProgramSelection = null;
      }
      return {
        proof,
        registrationVariant: "member",
        completionMode: "initial",
        profile,
        memberDetails,
        password,
      };
    }
  }
}

function buildFellowBirth(
  plan: VariantRenderPlan,
  rendered: ReadonlySet<string>,
  values: VariantFormValues,
  cleared: ReadonlySet<string>,
): { birthMonthDay?: string | null; birthYearRangeReference?: string | null } {
  const birth: { birthMonthDay?: string | null; birthYearRangeReference?: string | null } = {};
  if (rendered.has("fellowDetails.birthMonthDay")) {
    const birthMonthDay = textValue(values, "fellowDetails.birthMonthDay");
    if (birthMonthDay) {
      birth.birthMonthDay = birthMonthDay;
    } else if (cleared.has("fellowDetails.birthMonthDay")) {
      birth.birthMonthDay = null;
    }
  }
  const birthYearRangeReference = selectedReference(
    plan,
    values,
    "fellowDetails.birthYearRangeReference",
  );
  if (birthYearRangeReference) {
    birth.birthYearRangeReference = birthYearRangeReference;
  } else if (cleared.has("fellowDetails.birthYearRangeReference")) {
    birth.birthYearRangeReference = null;
  }
  return birth;
}

function wrapPublicationPreferences(
  wrapper: "fellowDetails" | "memberDetails",
  rendered: ReadonlySet<string>,
  values: VariantFormValues,
): { publicationPreferences?: PublicationPreferences } {
  const preferences = buildPublicationPreferences(wrapper, rendered, values);
  return preferences ? { publicationPreferences: preferences } : {};
}

const MEMBERSHIP_CATEGORY_LABELS: Record<string, string> = {
  standard: "Standard",
  premium: "Premium",
  mediator_arbitrator: "Mediator / Arbitrator",
};

const SOURCE_STATUS_LABELS: Record<string, string> = {
  invited: "New member registration",
  change_subscription: "Subscription change",
  renew_subscription: "Subscription renewal",
};

/**
 * Display-only server-bound company (UI contract, common profile): when the
 * server does NOT list `companyName` as editable but the lookup supplies a
 * bound company name, the registrant still sees their company context —
 * mirroring the staff-counsel office display pattern. Never renders the
 * company reference or any identity data, and has no completion-request echo.
 */
export function BoundCompanyPanel({
  lookup,
  plan,
}: {
  lookup: InvitationLookupReady;
  plan: VariantRenderPlan;
}): ReactElement | null {
  const companyName = lookup.prefill.company?.name;
  if (!companyName || plan.fields.some((definition) => definition.path === "companyName")) {
    return null;
  }
  return (
    <section aria-labelledby="registration-bound-company-heading" className="mb-6">
      <h2 id="registration-bound-company-heading" className="text-base font-semibold">
        Company
      </h2>
      <p className="mt-1 text-base text-gray-600">{companyName}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Your company is on file for this registration and can&apos;t be changed here.
      </p>
    </section>
  );
}

/**
 * Read-only variant context above the form: the single pre-approved
 * staff-counsel office (Direct, display-only) and the server-derived
 * membership/source categories (Member). Never renders IDs, references,
 * contact data, roles, statuses, or payment/provider fields.
 */
export function VariantContextPanel({
  lookup,
  plan,
}: {
  lookup: InvitationLookupReady;
  /** When supplied, the membership summary line is de-duplicated against a
   * rendered Member program selection field (011-15 F5, DECIDED 2026-09-04). */
  plan?: VariantRenderPlan;
}): ReactElement | null {
  switch (lookup.registrationVariant) {
    case "direct_fellow": {
      const staffCounsel = lookup.prefill.staffCounsel;
      if (staffCounsel?.approved !== true) {
        return null;
      }
      return (
        <section aria-labelledby="registration-staff-counsel-heading" className="mb-6">
          <h2 id="registration-staff-counsel-heading" className="text-base font-semibold">
            Staff counsel office
          </h2>
          <p className="mt-1 text-base text-gray-600">
            {staffCounsel.officeName ?? "A pre-approved office is on file for your registration."}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your office was pre-approved with this invitation and can&apos;t be changed here.
          </p>
        </section>
      );
    }
    case "member": {
      const category = lookup.prefill.membershipCategory;
      const sourceStatus = lookup.prefill.sourceStatusCategory;
      // 011-15 F5: when the editable Member program selection field renders,
      // the read-only "Membership" summary would duplicate it — the field is
      // the single program surface and the summary line is suppressed.
      const programFieldRendered =
        plan?.fields.some(
          (definition) => definition.path === "memberDetails.memberProgramSelection",
        ) ?? false;
      const showCategory = Boolean(category) && !programFieldRendered;
      if (!showCategory && !sourceStatus) {
        return null;
      }
      return (
        <dl className="mb-6 space-y-1 text-base text-gray-600">
          {showCategory && category && (
            <div>
              <dt className="inline font-medium text-gray-900">Membership: </dt>
              <dd className="inline">{MEMBERSHIP_CATEGORY_LABELS[category] ?? category}</dd>
            </div>
          )}
          {sourceStatus && (
            <div>
              <dt className="inline font-medium text-gray-900">Registration type: </dt>
              <dd className="inline">{SOURCE_STATUS_LABELS[sourceStatus] ?? sourceStatus}</dd>
            </div>
          )}
        </dl>
      );
    }
  }
}
